import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Boxes, ArrowDownUp, PackageCheck, History, CalendarClock, GitCompareArrows, LayoutDashboard, ArrowRightLeft, PackageSearch } from "lucide-react";
import { get } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtNum, todayIso, addDays } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { KpiTile } from "@/components/common/KpiTile";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { TableauDetail } from "@/components/analyse/TableauDetail";
import { MvtForm } from "@/components/inventaire/MvtForm";
import { SynchroStatut } from "@/components/inventaire/SynchroStatut";
import { FiltresMouvements } from "@/components/inventaire/FiltresMouvements";
import { ExportsInventaire } from "@/components/inventaire/ExportsInventaire";
import { TableauBordInventaire } from "@/components/inventaire/TableauBordInventaire";
import { TransfertsPanel } from "@/components/inventaire/TransfertsPanel";
import { TracabilitePanel } from "@/components/inventaire/TracabilitePanel";
import { cn } from "@/lib/utils";

const ONGLETS = [
  { key: "tableau", label: "Tableau de bord", icon: LayoutDashboard },
  { key: "stock", label: "Stock", icon: Boxes },
  { key: "mouvements", label: "Mouvements", icon: History },
  { key: "transferts", label: "Transferts", icon: ArrowRightLeft },
  { key: "lots", label: "Lots", icon: PackageCheck },
  { key: "tracabilite", label: "Traçabilité", icon: PackageSearch },
  { key: "date", label: "Stock à date", icon: CalendarClock },
  { key: "ecart", label: "Production vs inventaire", icon: GitCompareArrows },
];

const F_VIDE = { du: "", au: "", type: "", produit_id: "", emplacement_id: "", lot_id: "",
  of_id: "", utilisateur_id: "", unite: "" };

const nettoie = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v != null));

export default function InventairePage() {
  const { user } = useAuth();
  const [onglet, setOnglet] = useState("tableau");
  const [page, setPage] = useState(1);
  const [recherche, setRecherche] = useState("");
  const [f, setF] = useState(F_VIDE);
  const [dateRef, setDateRef] = useState(todayIso());
  const [periode, setPeriode] = useState({ du: addDays(todayIso(), -29), au: todayIso() });

  const feats = useQuery({ queryKey: ["features"], queryFn: () => get("/me/features") });
  const actif = feats.data?.modules?.inventory?.actif;
  const perms = feats.data?.permissions || [];
  const peutEcrire = perms.some((p) => p.startsWith("inventory.") &&
    ["issue", "adjust", "initialize", "transfer", "receive"].includes(p.split(".")[1]));
  const peutAuditer = perms.includes("inventory.audit");

  const on = (k) => !!actif && onglet === k;
  const config = useQuery({ queryKey: ["inv-config"], queryFn: () => get("/inventaire/config"), enabled: !!actif });
  const synchro = useQuery({ queryKey: ["inv-synchro"], queryFn: () => get("/inventaire/synchro-statut"),
    enabled: !!actif, refetchInterval: 60000 });
  const produits = useQuery({ queryKey: ["produits"], queryFn: () => get("/produits"), enabled: !!actif });
  const empl = useQuery({ queryKey: ["inv-empl"], queryFn: () => get("/inventaire/emplacements"), enabled: !!actif });
  const tousLots = useQuery({ queryKey: ["inv-lots-liste"], queryFn: () => get("/inventaire/lots"), enabled: !!actif });
  const ofs = useQuery({ queryKey: ["of-tous"], queryFn: () => get("/of"), enabled: !!actif });
  const users = useQuery({ queryKey: ["users"], queryFn: () => get("/users"), retry: false,
    enabled: !!actif && ["admin", "superadmin"].includes(user?.role) });

  const stock = useQuery({ queryKey: ["inv-stock", f.produit_id, f.emplacement_id], enabled: on("stock"),
    queryFn: () => get("/inventaire/stock", nettoie({ produit_id: f.produit_id, emplacement_id: f.emplacement_id })) });
  const mvts = useQuery({ queryKey: ["inv-mvts", f, page, recherche], enabled: on("mouvements"), keepPreviousData: true,
    queryFn: () => get("/inventaire/mouvements", { page, page_size: 25, ...nettoie(f), ...(recherche ? { recherche } : {}) }) });
  const lots = useQuery({ queryKey: ["inv-lots", f.produit_id], enabled: on("lots"),
    queryFn: () => get("/inventaire/lots", nettoie({ produit_id: f.produit_id })) });
  const aDate = useQuery({ queryKey: ["inv-date", dateRef], enabled: on("date"),
    queryFn: () => get("/inventaire/stock-a-date", { date_ref: dateRef }) });
  const ecart = useQuery({ queryKey: ["inv-ecart", periode], enabled: on("ecart"),
    queryFn: () => get("/inventaire/analyse-periode", periode) });

  if (feats.isLoading) return <LoadingState rows={4} />;
  if (!actif) return (
    <div data-testid="inventaire-non-souscrit" className="space-y-3">
      <PageHeader crumbs={[{ label: "ProdTrack" }, { label: "Inventaire" }]} title="Inventaire & Traçabilité" />
      <EmptyState title="Module non souscrit"
        hint="Le module « Inventaire & Traçabilité » n'est pas activé pour cette entreprise. Contactez ProdTrack pour l'activer ou demander un essai." />
    </div>
  );

  const courant = { stock, mouvements: mvts, lots, date: aDate, ecart }[onglet];
  const unites = [...new Set((stock.data?.par_unite || []).map((u) => u.unite)
    .concat((tousLots.data || []).map((l) => l.unite)))].filter(Boolean);
  const paramsExport = { stock: nettoie({ produit_id: f.produit_id, emplacement_id: f.emplacement_id }),
    mouvements: { ...nettoie(f), ...(recherche ? { recherche } : {}) },
    lots: nettoie({ produit_id: f.produit_id }), date: { date_ref: dateRef }, ecart: periode,
    tableau: {}, transferts: { type: "transfert" }, tracabilite: {} }[onglet];

  const voirMouvements = (produit_id) => { setF({ ...F_VIDE, produit_id: String(produit_id) }); setPage(1); setOnglet("mouvements"); };

  return (
    <div data-testid="inventaire-page" className="space-y-3">
      <PageHeader crumbs={[{ label: "ProdTrack" }, { label: "Inventaire" }]}
        title="Inventaire & Traçabilité"
        subtitle={`Source de vérité : les mouvements de stock · module activé le ${(config.data?.date_activation || "").slice(0, 10) || "—"}`}
        actions={<ExportsInventaire onglet={onglet} params={paramsExport} />} />

      <SynchroStatut statut={synchro.data} peutAuditer={peutAuditer} />

      <div data-testid="inv-onglets" className="panel px-1.5 py-1.5 flex flex-wrap gap-1 overflow-x-auto">
        {ONGLETS.map((o) => (
          <button key={o.key} data-testid={`inv-onglet-${o.key}`}
            onClick={() => { setOnglet(o.key); setPage(1); setRecherche(""); }}
            className={cn("h-7 px-3 text-[11px] font-semibold rounded-sm flex items-center gap-1.5 whitespace-nowrap",
              onglet === o.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground")}>
            <o.icon className="h-3.5 w-3.5" />{o.label}
          </button>
        ))}
      </div>

      {peutEcrire && ["stock", "mouvements"].includes(onglet) && (
        <Panel title="Nouveau mouvement" testId="panel-mvt" bodyClassName="p-3">
          <MvtForm produits={produits.data || []} emplacements={empl.data || []} lots={tousLots.data || []}
            motifs={config.data?.motifs_sortie || []} perms={perms}
            negatifAutorise={!!config.data?.autoriser_stock_negatif} />
        </Panel>
      )}

      {/* AUDIT INV V2 §16 — Tableau de bord Inventaire (W1). */}
      {onglet === "tableau" && <TableauBordInventaire />}

      {/* AUDIT INV V2 §8 — Vue Transferts dédiée (W6). */}
      {onglet === "transferts" && peutEcrire && (
        <TransfertsPanel produits={produits.data || []} emplacements={empl.data || []} lots={tousLots.data || []} />
      )}
      {onglet === "transferts" && !peutEcrire && (
        <EmptyState title="Permission requise" hint="Un transfert nécessite la permission `inventory.transfer`." />
      )}

      {/* AUDIT INV V2 §6.7 — Traçabilité (W7). */}
      {onglet === "tracabilite" && <TracabilitePanel lots={tousLots.data || []} />}

      {onglet === "mouvements" && (
        <Panel title="Filtres" testId="panel-filtres-mvt" bodyClassName="p-3">
          <FiltresMouvements f={f} setF={(v) => { setF(v); setPage(1); }} produits={produits.data || []}
            emplacements={empl.data || []} lots={tousLots.data || []} ofs={ofs.data || []}
            utilisateurs={users.data || []} unites={unites}
            types={config.data?.types_mouvement || []} />
        </Panel>
      )}

      {courant?.isLoading ? <LoadingState rows={5} /> : courant?.error ? <ErrorState error={courant.error} onRetry={courant.refetch} /> : (<>
        {onglet === "stock" && stock.data && (<>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <KpiTile testId="kpi-nb-produits" label="Produits en stock" value={fmtNum(stock.data.nb_produits)} icon={Boxes} />
            {stock.data.par_unite.map((u) => (
              <KpiTile key={u.unite} testId={`kpi-stock-${u.unite}`} label={`Stock physique (${u.unite})`}
                value={fmtNum(u.physique)} unit={u.unite} icon={ArrowDownUp}
                sub={`Disponible ${fmtNum(u.disponible)} · qualité ${fmtNum(u.attente_qualite)} · bloqué ${fmtNum(u.bloque)}`} />
            ))}
          </div>
          {stock.data.multi_unites && <div className="text-[11px] text-amber-400 font-mono">unités non additionnables : chaque unité est présentée séparément</div>}
          <TableauDetail testId="table-stock" titre="Stock par produit — cliquez une ligne pour voir ses mouvements"
            data={{ items: stock.data.lignes, total: stock.data.nb_produits, pages: 1 }}
            page={1} setPage={() => {}} recherche={recherche} setRecherche={setRecherche}
            onLigneClick={(l) => voirMouvements(l.produit_id)}
            actions={<select data-testid="stock-filtre-emplacement" value={f.emplacement_id}
              onChange={(e) => setF({ ...f, emplacement_id: e.target.value })}
              className="h-7 bg-input border border-border rounded-sm px-2 text-[11px]">
              <option value="">Tous les emplacements</option>
              {(empl.data || []).map((e) => <option key={e.id} value={e.id}>{e.nom}</option>)}
            </select>}
            colonnes={[{ cle: "code", label: "Produit" }, { cle: "designation", label: "Désignation" },
              { cle: "famille", label: "Famille" }, { cle: "unite", label: "Unité" },
              { cle: "physique", label: "Stock physique", align: "right", render: (l) => `${fmtNum(l.physique)} ${l.unite}` },
              { cle: "disponible", label: "Disponible", align: "right", render: (l) => `${fmtNum(l.disponible)} ${l.unite}` },
              { cle: "attente_qualite", label: "Attente qualité", align: "right", render: (l) => fmtNum(l.attente_qualite) },
              { cle: "bloque", label: "Bloqué", align: "right", render: (l) => fmtNum(l.bloque) },
              { cle: "derniere_entree", label: "Dernière entrée" }, { cle: "derniere_sortie", label: "Dernière sortie" }]} />
        </>)}

        {onglet === "mouvements" && (
          <TableauDetail testId="table-mouvements" titre="Historique des mouvements" data={mvts.data}
            page={page} setPage={setPage} recherche={recherche} setRecherche={setRecherche}
            colonnes={[{ cle: "horodatage", label: "Date/heure" }, { cle: "type", label: "Type" },
              { cle: "produit", label: "Produit" }, { cle: "lot", label: "Lot" }, { cle: "of", label: "OF" },
              { cle: "quantite", label: "Quantité", align: "right", render: (m) => `${m.quantite > 0 ? "+" : ""}${fmtNum(m.quantite)} ${m.unite}` },
              { cle: "origine", label: "Origine" }, { cle: "destination", label: "Destination" },
              { cle: "statut_stock", label: "Statut" }, { cle: "utilisateur", label: "Utilisateur" },
              { cle: "motif", label: "Motif" },
              { cle: "reference_externe", label: "Référence" }, { cle: "commentaire", label: "Commentaire" }]} />
        )}

        {onglet === "lots" && lots.data && (
          <TableauDetail testId="table-lots" titre={`Lots (${lots.data.length})`}
            data={{ items: lots.data, total: lots.data.length, pages: 1 }}
            page={1} setPage={() => {}} recherche={recherche} setRecherche={setRecherche}
            onLigneClick={(l) => { setF({ ...F_VIDE, lot_id: String(l.id) }); setPage(1); setOnglet("mouvements"); }}
            colonnes={[{ cle: "code", label: "Lot" }, { cle: "produit", label: "Produit" }, { cle: "of", label: "OF" },
              { cle: "ligne", label: "Ligne" },
              { cle: "entre", label: "Entré en stock", align: "right", render: (l) => `${fmtNum(l.entre)} ${l.unite}` },
              { cle: "sorti", label: "Sorti", align: "right", render: (l) => `${fmtNum(l.sorti)} ${l.unite}` },
              { cle: "disponible", label: "Disponible", align: "right", render: (l) => `${fmtNum(l.disponible)} ${l.unite}` },
              { cle: "statut", label: "Statut" }]} />
        )}

        {onglet === "date" && (
          <TableauDetail testId="table-stock-date" titre={`Stock reconstruit au ${dateRef} 23:59`}
            data={{ items: aDate.data?.lignes || [], total: (aDate.data?.lignes || []).length, pages: 1 }}
            page={1} setPage={() => {}} recherche={recherche} setRecherche={setRecherche}
            actions={<input data-testid="input-date-ref" type="date" value={dateRef}
              onChange={(e) => setDateRef(e.target.value)}
              className="h-7 bg-input border border-border rounded-sm px-2 font-mono text-[11px]" />}
            colonnes={[{ cle: "code", label: "Produit" }, { cle: "unite", label: "Unité" },
              { cle: "physique", label: "Stock physique", align: "right", render: (l) => `${fmtNum(l.physique)} ${l.unite}` },
              { cle: "disponible", label: "Disponible", align: "right", render: (l) => `${fmtNum(l.disponible)} ${l.unite}` }]} />
        )}

        {onglet === "ecart" && (
          <TableauDetail testId="table-ecart" titre="Production vs inventaire"
            data={{ items: ecart.data?.lignes || [], total: (ecart.data?.lignes || []).length, pages: 1 }}
            page={1} setPage={() => {}} recherche={recherche} setRecherche={setRecherche}
            actions={<div className="flex items-center gap-1">
              <input data-testid="ecart-du" type="date" value={periode.du}
                onChange={(e) => setPeriode({ ...periode, du: e.target.value })}
                className="h-7 bg-input border border-border rounded-sm px-2 font-mono text-[11px]" />
              <input data-testid="ecart-au" type="date" value={periode.au}
                onChange={(e) => setPeriode({ ...periode, au: e.target.value })}
                className="h-7 bg-input border border-border rounded-sm px-2 font-mono text-[11px]" />
            </div>}
            colonnes={[{ cle: "produit", label: "Produit" }, { cle: "unite", label: "Unité" },
              { cle: "stock_debut", label: "Stock début", align: "right", render: (l) => fmtNum(l.stock_debut) },
              { cle: "production_bonne", label: "Production bonne", align: "right", render: (l) => `+${fmtNum(l.production_bonne)}` },
              { cle: "entrees", label: "Entrées stock", align: "right", render: (l) => `+${fmtNum(l.entrees)}` },
              { cle: "sorties", label: "Sorties", align: "right", render: (l) => `-${fmtNum(l.sorties)}` },
              { cle: "ajustements", label: "Ajustements", align: "right", render: (l) => fmtNum(l.ajustements) },
              { cle: "stock_fin", label: "Stock fin", align: "right", render: (l) => `${fmtNum(l.stock_fin)} ${l.unite}` },
              { cle: "ecart_production_inventaire", label: "Écart", align: "right", render: (l) => fmtNum(l.ecart_production_inventaire) }]} />
        )}
      </>)}
    </div>
  );
}
